/**
 * Leaderboard view helpers (pure — unit tested).
 *
 * The board itself comes from GET /gamification/leaderboard, which lists only
 * users who opted in on the server: their public profile is public AND shows
 * authority scores, AND they opted in to discovery. The same rule decides the `top_10_leaderboard` badge.
 */

import type { LeaderboardItem } from '../store/api/gamificationApi';
import type { LeaderboardEntry } from '../components/leaderboard/LeaderboardTable';

/** The settings page where a user opts in to (or out of) the leaderboard. */
export const LEADERBOARD_OPT_IN_SETTINGS_PATH = '/settings/public-profile';

export function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Map backend leaderboard items to table rows. Ranks come from the server.
 * The board publishes no user ids: the viewer's own row (isCurrentUser) is
 * keyed by their own id, every other row by a synthetic row key.
 */
export function toLeaderboardEntries(
  items: LeaderboardItem[] | undefined,
  currentUserId?: string | null
): LeaderboardEntry[] {
  if (!items) return [];
  return items.map((item, index) => ({
    userId: item.isCurrentUser && currentUserId ? currentUserId : `row-${index}-${item.publicSlug ?? 'anon'}`,
    displayName: item.name,
    avatarInitials: getInitials(item.name),
    authorityScore: item.avgScore,
    rank: item.rank,
    publicSlug: item.publicSlug,
    sessionsCount: item.sessionCount,
  }));
}

/** The current user's rank on the board, or null when they are not listed. */
export function findCurrentUserRank(
  entries: LeaderboardEntry[],
  currentUserId: string | null | undefined
): number | null {
  if (!currentUserId) return null;
  return entries.find((e) => e.userId === currentUserId)?.rank ?? null;
}

/**
 * Whether the user has opted in to the leaderboard, from their persisted public
 * profile settings AND their talent-discovery opt-in (the server requires
 * both). `null` while unknown (either not loaded).
 */
export function isLeaderboardOptedIn(
  settings: { isPublic: boolean; visibility?: { showAuthorityScores?: boolean } | null } | null | undefined,
  discovery: { optedIn: boolean } | null | undefined
): boolean | null {
  if (!settings || discovery === undefined) return null;
  return (
    settings.isPublic === true &&
    settings.visibility?.showAuthorityScores === true &&
    discovery?.optedIn === true
  );
}
